/**
 * ScanHistoryScreen — FE #124.3.
 *
 * Route: `/scans`. Lists every scan the user has run, grouped by
 * status. Reached from three entry points (USJ.md J2.B4 v2):
 *   - Dashboard recovery banner ([View all N →])
 *   - [+ Add New Item] sheet ("Resume scan (N unfinished)" row)
 *   - Top-bar 🕒 clock-rewind icon (always visible across all tabs)
 *
 * Layout:
 *   ┌────────────────────────────────────────────┐
 *   │ ←  Scan history                             │
 *   │ 1 in progress · 2 ready to save · 9 done    │
 *   │ ──────────────────────────────              │
 *   │ ┌────────────────────────────────────────┐ │
 *   │ │ [photo] Dining room                  🔵│ │
 *   │ │         12 items detected              │ │
 *   │ │         ▓▓▓▓▓▓░░░░ scanning…           │ │
 *   │ │         Started 47s ago         [View] │ │
 *   │ └────────────────────────────────────────┘ │
 *   │ ┌────────────────────────────────────────┐ │
 *   │ │ [photo] Hallway                       ⚠│ │
 *   │ │         8 items · ready to save        │ │
 *   │ │         Pending 12 min ago    [Save →] │ │
 *   │ └────────────────────────────────────────┘ │
 *   │ ─── Completed ──────────────────────         │
 *   │ ...                                          │
 *   │              [ Load more ]                   │
 *   └────────────────────────────────────────────┘
 *
 * Live polling: every 2s, refresh any IN_PROGRESS scan via the
 * scansSlice.refreshScan thunk. Max 1 in-flight cycle. Auto-stops
 * when no scans remain IN_PROGRESS (the slice handles that — once
 * a row's status flips, refreshScan.fulfilled drops it from
 * pollingIds).
 *
 * Discard: long-press a row → native confirm → optimistic remove +
 * DELETE /users/scans/{id}. Roll back on BE rejection.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  FlatList,
  Platform,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useIsFocused, useNavigation } from '@react-navigation/native';

import { goBackOrHome } from '../../navigation/goBackOrHome';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { fpColors } from '../../styles/theme';
import {
  loadScans,
  refreshScan,
  discardScan,
  optimisticallyRemoveScan,
  restoreScan,
  selectScansList,
  selectScansLoading,
  selectScansLoadingMore,
  selectScansHasMore,
  selectScansError,
  selectScansByStatus,
  selectInProgressIds,
  selectScansState,
} from './scansSlice';
import { ScanRow } from './components/ScanRow';
import { FilterRow } from '../../components/FilterRow';
import type { ScanHistoryItem } from '../../api/types';

const POLL_INTERVAL_MS = 2000;

export const ScanHistoryScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();

  const allScans = useAppSelector(selectScansList);
  const loading = useAppSelector(selectScansLoading);
  const loadingMore = useAppSelector(selectScansLoadingMore);
  const hasMore = useAppSelector(selectScansHasMore);
  const error = useAppSelector(selectScansError);
  const buckets = useAppSelector(selectScansByStatus);
  const inProgressIds = useAppSelector(selectInProgressIds);
  const scansState = useAppSelector(selectScansState);

  /* ────────────────────────────────────────────────────────────
   * Initial load + refresh-on-mount
   * ──────────────────────────────────────────────────────────── */
  useEffect(() => {
    dispatch(loadScans({ mode: 'replace' }));
  }, [dispatch]);

  /* ────────────────────────────────────────────────────────────
   * Live polling for IN_PROGRESS rows.
   *
   * On every interval tick, fire refreshScan(id) for each
   * IN_PROGRESS scan. Max 1 in-flight cycle prevents stampeding.
   * Auto-stops via the slice's status-flip detection.
   *
   * Gated on `useIsFocused` so the interval only ticks while the
   * /scans screen is in front. Without this gate the poll fires
   * while the user is mid-navigation to SegmentScreen — the
   * refreshScan dispatch lands on a row mid-unmount and bricks
   * the navigation animation. FE #144.
   * ──────────────────────────────────────────────────────────── */
  const inFlightRef = useRef<boolean>(false);
  const isFocused = useIsFocused();

  useEffect(() => {
    if (!isFocused) return;
    if (inProgressIds.length === 0) return;

    const tick = async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        await Promise.all(
          inProgressIds.map((id) => dispatch(refreshScan(id))),
        );
      } finally {
        inFlightRef.current = false;
      }
    };

    const interval = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
    // Re-bind whenever the set of in-progress ids changes OR focus
    // flips. Losing focus clears the interval immediately.
  }, [dispatch, inProgressIds, isFocused]);

  /* ────────────────────────────────────────────────────────────
   * Handlers
   * ──────────────────────────────────────────────────────────── */

  const handleRefresh = useCallback(() => {
    dispatch(loadScans({ mode: 'replace' }));
  }, [dispatch]);

  const handleLoadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    dispatch(loadScans({ mode: 'append' }));
  }, [dispatch, loadingMore, hasMore]);

  /** Row tap → status-based nav. Per USJ.md J2.B4 v2's row matrix. */
  const handleRowPress = useCallback(
    (scan: ScanHistoryItem) => {
      switch (scan.status) {
        case 'IN_PROGRESS':
        case 'READY_TO_SAVE':
        case 'FAILED':
          // All three resume into SegmentScreen with the file_record
          // loaded. The screen's resume effect (FE #124.6) decides
          // whether to re-attach SSE (IN_PROGRESS), render the [Save]
          // CTA (READY_TO_SAVE), or surface [Retry] (FAILED).
          navigation.navigate('Upload', {
            file_record_id: scan.file_record_id,
            resume: true,
          });
          break;
        case 'COMPLETED':
          // Read-only scene view. The user can also reach this via
          // the Items grid's Scenes layout, but tapping a COMPLETED
          // row here is the canonical entry point.
          navigation.navigate('SceneViewer', {
            fileId: scan.file_record_id,
          });
          break;
        case 'CANCELLED':
          // No-op (cancelled rows are filtered out by default; this
          // is just a safety guard).
          break;
      }
    },
    [navigation],
  );

  /** Long-press → discard confirm. Optimistic — remove then call BE;
   *  roll back on failure. */
  const handleRowLongPress = useCallback(
    (scan: ScanHistoryItem) => {
      const itemCount =
        scan.status === 'COMPLETED' ? scan.saved_count : scan.detected_count;
      const message =
        scan.status === 'COMPLETED'
          ? 'Delete this scan record? The saved items will stay.'
          : `Discard this scan? ${itemCount} item${itemCount === 1 ? '' : 's'} detected, not saved. This can't be undone.`;

      const performDiscard = async () => {
        // Snapshot for rollback.
        const snapshot = scansState.byId[scan.file_record_id];
        const originalIdx = scansState.listOrder.indexOf(scan.file_record_id);
        if (!snapshot || originalIdx < 0) return;

        dispatch(optimisticallyRemoveScan(scan.file_record_id));
        try {
          await dispatch(discardScan(scan.file_record_id)).unwrap();
        } catch {
          // Roll back — restore the row at its original sort position.
          dispatch(
            restoreScan({
              id: scan.file_record_id,
              row: snapshot,
              idx: originalIdx,
            }),
          );
          Alert.alert(
            'Couldn’t discard',
            'The scan is still in your history. Try again.',
          );
        }
      };

      // Web vs native confirm. Web uses window.confirm (synchronous);
      // native uses Alert.alert. The pattern mirrors BrowseScreen's
      // handleDelete (FE-250).
      if (typeof window !== 'undefined' && window.confirm) {
        if (window.confirm(message)) performDiscard();
      } else {
        Alert.alert('Discard this scan?', message, [
          { text: 'Keep', style: 'cancel' },
          { text: 'Discard', style: 'destructive', onPress: performDiscard },
        ]);
      }
    },
    [dispatch, scansState],
  );

  /* ────────────────────────────────────────────────────────────
   * Summary line — "1 in progress · 2 ready to save · 9 done"
   * ──────────────────────────────────────────────────────────── */
  const summaryLine = useMemo(() => {
    const parts: string[] = [];
    if (buckets.inProgress.length > 0)
      parts.push(`${buckets.inProgress.length} in progress`);
    if (buckets.readyToSave.length > 0)
      parts.push(`${buckets.readyToSave.length} ready to save`);
    if (buckets.failed.length > 0)
      parts.push(`${buckets.failed.length} failed`);
    if (buckets.completed.length > 0)
      parts.push(`${buckets.completed.length} done`);
    return parts.join(' · ');
  }, [buckets]);

  /* ────────────────────────────────────────────────────────────
   * Render
   * ──────────────────────────────────────────────────────────── */

  // We render with section breaks: live + pending + failed first,
  // then a "Completed" divider, then completed rows. CANCELLED rows
  // are hidden by default (don't surface user's own discards).
  //
  // The BE returns scans sorted by created_at desc (default), which
  // means COMPLETED rows can interleave with READY_TO_SAVE rows by
  // timestamp. We re-bucket here so the divider fires exactly once
  // at the IN_PROGRESS/READY_TO_SAVE/FAILED → COMPLETED boundary,
  // not once per status transition in the timeline.
  // Search-UX §3.9 — filter chrome local state.  Status chips +
  // date chips + free-text filename search.  Mirrors GitHub
  // Actions' runs page where status is a chip and date is a
  // separate facet — the literal "status × date × filename"
  // lookup the audit calls out as the scan-debug pain point.
  const [scanSearch, setScanSearch] = useState('');
  const [statusChip, setStatusChip] = useState<string>('all');
  const [dateChip, setDateChip] = useState<string>('all');

  const visibleScans = useMemo(() => {
    const STATUS_RANK: Record<string, number> = {
      IN_PROGRESS: 0,
      READY_TO_SAVE: 1,
      FAILED: 2,
      COMPLETED: 3,
      // CANCELLED filtered out below.
    };
    const q = scanSearch.trim().toLowerCase();
    const now = Date.now();
    const WEEK = 7 * 24 * 60 * 60 * 1000;
    const MONTH = 30 * 24 * 60 * 60 * 1000;
    return allScans
      .filter((s) => s.status !== 'CANCELLED')
      .filter((s: any) => {
        if (statusChip === 'all') return true;
        if (statusChip === 'failed') return s.status === 'FAILED';
        if (statusChip === 'partial') return s.status === 'READY_TO_SAVE';
        if (statusChip === 'complete') return s.status === 'COMPLETED';
        return true;
      })
      .filter((s: any) => {
        if (dateChip === 'all') return true;
        const t = new Date(s.last_event_at).getTime();
        if (Number.isNaN(t)) return false;
        if (dateChip === 'week') return now - t <= WEEK;
        if (dateChip === 'month') return now - t <= MONTH;
        return true;
      })
      .filter((s: any) => {
        if (!q) return true;
        const name = String(
          s.filename ?? s.original_filename ?? '',
        ).toLowerCase();
        return name.includes(q);
      })
      .slice()
      .sort((a, b) => {
        const ra = STATUS_RANK[a.status] ?? 99;
        const rb = STATUS_RANK[b.status] ?? 99;
        if (ra !== rb) return ra - rb;
        // Within a bucket, newest first (matches BE default sort).
        const ta = new Date(a.last_event_at).getTime();
        const tb = new Date(b.last_event_at).getTime();
        return tb - ta;
      });
  }, [allScans, scanSearch, statusChip, dateChip]);

  /** Section labels per status. Headers fire on bucket-boundary
   *  transitions in renderItem. Followed pattern from Apple Mail's
   *  TODAY/YESTERDAY/EARLIER and Things 3's Today/Anytime/Upcoming. */
  const SECTION_LABEL: Record<ScanHistoryItem['status'], string> = {
    IN_PROGRESS: 'Scanning now',
    READY_TO_SAVE: 'Ready to save',
    FAILED: 'Failed — retry available',
    COMPLETED: 'Completed',
    CANCELLED: '', // filtered out, never reached
  };

  /** Per-status row counts for the header subtitle ("9 ready · ..."). */
  const sectionCounts = useMemo(() => {
    const acc: Partial<Record<ScanHistoryItem['status'], number>> = {};
    for (const s of visibleScans) {
      acc[s.status] = (acc[s.status] ?? 0) + 1;
    }
    return acc;
  }, [visibleScans]);

  const renderItem = useCallback(
    ({ item, index }: { item: ScanHistoryItem; index: number }) => {
      // Insert a section header before the first row of each bucket
      // (IN_PROGRESS / READY_TO_SAVE / FAILED / COMPLETED). Matches
      // the Apple Mail / Things 3 / GitHub Issues pattern.
      const prevStatus = index > 0 ? visibleScans[index - 1]?.status : null;
      const isFirstOfBucket = item.status !== prevStatus;
      const count = sectionCounts[item.status] ?? 0;
      const label = SECTION_LABEL[item.status];
      return (
        <>
          {isFirstOfBucket && label ? (
            <View style={styles.sectionBreak}>
              <View style={styles.sectionLine} />
              <Text style={styles.sectionLabel}>
                {label}
                {count > 1 ? ` · ${count}` : ''}
              </Text>
              <View style={styles.sectionLine} />
            </View>
          ) : null}
          <ScanRow
            scan={item}
            onPress={handleRowPress}
            onLongPress={handleRowLongPress}
            onDiscard={handleRowLongPress}
            discarding={scansState.discardingById[item.file_record_id]}
          />
        </>
      );
    },
    [
      visibleScans,
      sectionCounts,
      handleRowPress,
      handleRowLongPress,
      scansState.discardingById,
    ],
  );

  const renderEmpty = useCallback(() => {
    if (loading) return null;
    return (
      <View style={styles.emptyState}>
        <View style={styles.emptyIconWrap}>
          <MaterialIcons
            name="photo-library"
            size={36}
            color={fpColors.onSurfaceVariant}
          />
        </View>
        <Text style={styles.emptyTitle}>No scans yet</Text>
        <Text style={styles.emptyBody}>
          Tap the [+ Add New Item] button on the Dashboard to scan your
          first item or room.
        </Text>
      </View>
    );
  }, [loading]);

  const renderFooter = useCallback(() => {
    if (!hasMore && visibleScans.length === 0) return null;
    if (loadingMore) {
      return (
        <View style={styles.footerLoading}>
          <ActivityIndicator size="small" color={fpColors.primary} />
        </View>
      );
    }
    if (hasMore) {
      return (
        <Pressable
          onPress={handleLoadMore}
          style={({ pressed }) => [
            styles.loadMoreBtn,
            pressed && { opacity: 0.7 },
          ]}
          accessibilityRole="button"
          accessibilityLabel="Load more scans"
          testID="scans-load-more"
        >
          <Text style={styles.loadMoreText}>Load more</Text>
        </Pressable>
      );
    }
    return null;
  }, [hasMore, loadingMore, visibleScans.length, handleLoadMore]);

  return (
    <View style={styles.page}>
      {/* Header */}
      <View style={styles.topBar}>
        <Pressable
          onPress={() => goBackOrHome(navigation)}
          style={styles.backBtn}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Go back"
          testID="scans-back"
        >
          <MaterialIcons
            name="arrow-back"
            size={22}
            color={fpColors.onSurface}
          />
        </Pressable>
        <View style={{ flex: 1 }}>
          <Text style={styles.topTitle}>Scan history</Text>
          {summaryLine ? (
            <Text style={styles.summaryLine} numberOfLines={1}>
              {summaryLine}
            </Text>
          ) : null}
        </View>
      </View>

      {/* Body */}
      {loading && visibleScans.length === 0 ? (
        <View style={styles.loadingState}>
          <ActivityIndicator size="large" color={fpColors.primary} />
        </View>
      ) : error && visibleScans.length === 0 ? (
        <View style={styles.errorState}>
          <MaterialIcons
            name="error-outline"
            size={32}
            color={fpColors.error}
          />
          <Text style={styles.errorTitle}>Couldn’t load scans</Text>
          <Text style={styles.errorBody}>{error}</Text>
          <Pressable
            onPress={handleRefresh}
            style={styles.retryBtn}
            accessibilityRole="button"
          >
            <Text style={styles.retryText}>Retry</Text>
          </Pressable>
        </View>
      ) : (
        <FlatList
          data={visibleScans}
          keyExtractor={(s) => String(s.file_record_id)}
          renderItem={renderItem}
          ListHeaderComponent={
            allScans.length > 0 ? (
              // Search-UX §3.9 — status + date + filename filter row.
              // Mirrors GitHub Actions runs page: status chip + date
              // facet + free-text input.  Hidden until any scans
              // have loaded so the empty-state copy isn't cluttered.
              <FilterRow
                query={scanSearch}
                onQueryChange={setScanSearch}
                placeholder="Search by filename"
                chips={[
                  { key: 'all', label: 'All' },
                  { key: 'failed', label: 'Failed' },
                  { key: 'partial', label: 'Partial' },
                  { key: 'complete', label: 'Complete' },
                ]}
                activeKey={statusChip}
                onChipPress={setStatusChip}
                chipsSecondary={[
                  { key: 'all', label: 'All time' },
                  { key: 'week', label: 'This week' },
                  { key: 'month', label: 'This month' },
                ]}
                activeSecondaryKey={dateChip}
                onSecondaryChipPress={setDateChip}
                testID="scan-history-filter"
              />
            ) : null
          }
          refreshControl={
            <RefreshControl
              refreshing={loading && visibleScans.length > 0}
              onRefresh={handleRefresh}
              tintColor={fpColors.primary}
            />
          }
          ListEmptyComponent={renderEmpty}
          ListFooterComponent={renderFooter}
          contentContainerStyle={
            visibleScans.length === 0 ? styles.emptyContainer : undefined
          }
          testID="scans-list"
        />
      )}
    </View>
  );
};

/* ──────────────────────────────────────────────────────────────────
 * Styles
 * ────────────────────────────────────────────────────────────────── */

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: fpColors.background },
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingTop: Platform.OS === 'ios' ? 54 : 18,
    paddingBottom: 14,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
  },
  backBtn: { padding: 4 },
  topTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 18,
    color: fpColors.onSurface,
  },
  summaryLine: {
    marginTop: 2,
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
  },
  loadingState: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  errorState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
    gap: 8,
  },
  errorTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 15,
    color: fpColors.onSurface,
  },
  errorBody: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
  },
  retryBtn: {
    marginTop: 12,
    paddingHorizontal: 18,
    paddingVertical: 8,
    backgroundColor: fpColors.primary,
    borderRadius: 999,
  },
  retryText: {
    fontFamily: 'Inter_700Bold',
    color: fpColors.onPrimary,
    fontSize: 13,
  },
  emptyContainer: { flex: 1 },
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
    gap: 8,
  },
  emptyIconWrap: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: fpColors.surfaceContainerLow,
    marginBottom: 4,
  },
  emptyTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: fpColors.onSurface,
  },
  emptyBody: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
    lineHeight: 18,
  },
  sectionBreak: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
    backgroundColor: fpColors.background,
  },
  sectionLine: {
    flex: 1,
    height: 1,
    backgroundColor: fpColors.outlineVariant,
  },
  sectionLabel: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  footerLoading: { paddingVertical: 18, alignItems: 'center' },
  loadMoreBtn: {
    margin: 18,
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: fpColors.outline,
    borderRadius: 8,
    alignItems: 'center',
    backgroundColor: fpColors.surface,
  },
  loadMoreText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.primary,
  },
});
